import { type ReactNode } from "react";

import classes from "./TreeTable.module.less";
import { ToggleIcon } from "../icon/ToggleIcon";
import { IconButton } from "~/common/mui";
import classNames from "classnames";

export interface TreeTableNode<T extends TreeTableNode<T>> {
  id: string;
  children: T[];
  expanded?: boolean;
}

export interface TreeTableColumnDef<T extends TreeTableNode<T>> {
  title: ReactNode | (() => ReactNode);
  width: string;
  headerClass?: string;
  bodyClass?: string;
  render: keyof T | ((row: T) => ReactNode);
}

interface TreeTableProps<T extends TreeTableNode<T>> {
  className?: string;
  data: TreeTableNode<T> | TreeTableNode<T>[];
  colDefs: TreeTableColumnDef<T>[];
  indentation?: number;
  onToggleExpand: (nodeId: string, newValue: boolean) => void;
}

export function TreeTable<T extends TreeTableNode<T>>(props: TreeTableProps<T>) {
  const indentation = props.indentation ?? 16;

  const data = Array.isArray(props.data) ? props.data : [props.data];

  return (
    <div className={classNames(classes.table, props.className)}>
      <TreeTableHeader {...props} />
      {data.map((node) => (
        <TTNode key={node.id} {...props} cumulativeIndentation={0} indentation={indentation} data={node} />
      ))}
    </div>
  );
}

function TreeTableHeader<T extends TreeTableNode<T>>(props: TreeTableProps<T>) {
  return (
    <div className={classes.header}>
      {props.colDefs.map((def, index) => {
        return (
          <div key={index} style={{ width: def.width }} className={classes.headerCell}>
            {typeof def.title === "function" ? def.title() : def.title}
          </div>
        );
      })}
    </div>
  );
}

type TreeTableNodeProps<T extends TreeTableNode<T>> = Omit<TreeTableProps<T>, "className" | "indendation" | "data"> & {
  indentation: number;
  cumulativeIndentation: number;
  data: TreeTableNode<T>;
};

function TTNode<T extends TreeTableNode<T>>(props: TreeTableNodeProps<T>) {
  const renderCell = (def: TreeTableColumnDef<T>, data: TreeTableNode<T>) => {
    // @ts-ignore
    return typeof def.render === "function" ? def.render(data) : data[def.render];
  };

  const getCellWidth = (index: number, def: TreeTableColumnDef<T>) => {
    return index === 0 ? `calc(${def.width} - ${props.cumulativeIndentation}px` : def.width;
  };

  const showExpandButton = props.data.children.length > 0;

  return (
    <>
      <div className={classes.row}>
        {props.colDefs.map((def, index) => (
          <>
            {index === 0 && <div style={{ width: `${props.cumulativeIndentation}px` }} />}
            <div className={classes.bodyCell} style={{ width: getCellWidth(index, def) }}>
              {index === 0 && (
                <IconButton
                  className={classes.expandIcon}
                  onClick={() => props.onToggleExpand(props.data.id, !props.data.expanded)}
                  color={"primary"}
                  style={{ visibility: showExpandButton ? "visible" : "hidden" }}
                >
                  <ToggleIcon isActive={!!props.data.expanded} />
                </IconButton>
              )}
              {renderCell(def, props.data)}
            </div>
          </>
        ))}
      </div>
      {props.data.expanded &&
        props.data.children.map((child, index) => (
          <TTNode
            key={child.id}
            {...props}
            data={child}
            cumulativeIndentation={props.cumulativeIndentation + props.indentation}
          />
        ))}
    </>
  );
}
